/**
 * 「新しく登録される方」の一覧（issue #203・docs/01 6-4）。
 *
 * 取り込みの画面は「**押す前に、何件がどうなるかを必ずお見せします**」と約束しています。
 * ところが件数だけでは、**すでに名簿にいる方の写しが黙って増える**ことに気づけません
 * （導入時に電話番号なしで作った管理者などは、突き合わせる鍵が無いので別人になります）。
 *
 * そこで、どなたが新しく登録されるかを**お名前・スタッフID・拠点**で並べ、
 * 同じお名前の方がすでに名簿にいる行には、その場で注意をお出しします。
 */
import type { ReactNode } from 'react';
import type { RosterPreviewedCreate } from '../api/types.js';
import { DataTable } from './ui/DataTable.js';

/** 一度に開いて見せる人数（これを超えたら折りたたむ）。 */
const VISIBLE_WITHOUT_OPENING = 20;

export function RosterCreateTable({ creates }: { creates: RosterPreviewedCreate[] }): ReactNode {
  if (creates.length === 0) return null;
  const warned = creates.filter((create) => create.sameNameExisting !== undefined);
  const table = (
    <DataTable label="新しく登録される方">
      <thead>
        <tr>
          <th scope="col">CSVの行</th>
          <th scope="col">お名前</th>
          <th scope="col">スタッフID</th>
          <th scope="col">拠点</th>
          <th scope="col">気をつけたいこと</th>
        </tr>
      </thead>
      <tbody>
        {creates.map((create) => (
          <tr key={create.lineNumber}>
            <td>{create.lineNumber}行目</td>
            <th scope="row">{create.displayName}</th>
            <td>{create.staffCode ?? '（なし）'}</td>
            <td className="ui-cell-soft">{create.siteName ?? '（指定なし）'}</td>
            <td className="ui-cell-wrap">
              {create.sameNameExisting ? (
                <strong>
                  {`同じお名前の方が、すでにメンバーにいます（${
                    create.sameNameExisting.siteName ?? '拠点の指定なし'
                  }）。別の方であればこのままで、同じ方であれば「やめる」を押し、` +
                    'その方の画面から電話番号を登録してからもう一度お取り込みください。'}
                </strong>
              ) : (
                '—'
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </DataTable>
  );

  return (
    <section>
      <h3>新しく登録される方（{creates.length}名）</h3>
      {warned.length > 0 ? (
        <p>
          <strong>このうち{warned.length}名は、同じお名前の方がすでにメンバーにいます。</strong>
          そのまま取り込むと、同じ方が2行になることがあります。
        </p>
      ) : null}
      {creates.length > VISIBLE_WITHOUT_OPENING ? (
        <details>
          <summary>{creates.length}名すべてを見る</summary>
          {table}
        </details>
      ) : (
        table
      )}
    </section>
  );
}
